import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { DateTime } from 'luxon';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { uploadDataDetailsSaga } from './saga';
import {
  emailCreateEditActions,
  reducer,
  sliceKey,
} from 'app/pages/EmailCreateEditPage/slice';
import {
  selectCreateEditEmailTemplate,
  selectSavedEmailTemplate,
} from 'app/pages/EmailCreateEditPage/selectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import LoadingIndicator from 'app/components/LoadingIndicator';
import { useStyles } from 'app/pages/EmailCreateEditPage/style';
import {
  DataTableContainer,
  PageContainer,
  PageHeader,
  BreadcrumbContainer,
  BreadcrumbLinkComponent,
} from 'app/components/StyledComponents';
import { DataGridComponent } from 'app/components/DataGridComponent/Loadable';
import { StepperComponent } from 'app/components/StepperComponent/Loadable';
import Step1Container from 'app/pages/EmailCreateEditPage/component/Step1Container';
import { useTemplateTypes } from 'app/pages/EmailCreateEditPage/hooks/useTemplateTypes';
import { useTemplateTypeRules } from 'app/pages/EmailCreateEditPage/hooks/useTemplateTypeRules';
import Step2Container from 'app/pages/EmailCreateEditPage/component/Step2Container';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
  GridColDef,
  GridSortModel,
  GridFilterModel,
  GridColumnHeaderParams,
} from '@mui/x-data-grid';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

const steps = ['Template', 'Save Template'];

export type EmailTemplatesCreateEditPageProps = {
  /** When true, hides Helmet + PageContainer + breadcrumbs/header for embedding in modals. */
  embedded?: boolean;
  /** Optional type prefill (e.g. CPO_VALUE) for create flow. */
  initialTemplateType?: string;
  /** When set, template generation preferences show only this segment. */
  filterSegmentName?: string;
};

export function EmailTemplatesCreateEditPage({
  embedded = false,
  initialTemplateType,
  filterSegmentName,
}: EmailTemplatesCreateEditPageProps) {
  const classes = useStyles();

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: uploadDataDetailsSaga });

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { email_template_id }: any = useParams();
  const createEditTemplateDetails = useSelector(selectCreateEditEmailTemplate);
  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerId = selectedDealer.dealer || '';
  const templateDetails = useSelector(selectSavedEmailTemplate);
  const template = templateDetails.data?.body || {};

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const getDefaultExpirationDate = () =>
    DateTime.now().plus({ weeks: 1 }).startOf('day').toISODate() ?? '';

  const [templateState, setTemplateState] = React.useState({
    type: '',
    generated_type: '',
    applicable_types: [] as string[],
    name: '',
    subject: '',
    html: '',
    expiration_date: getDefaultExpirationDate(),
    tracking_id: '',
  });
  const [htmlFile, setHtmlFile] = React.useState<File | null>(null);
  const [openDialogSaveConfirmation, setOpenDialogSaveConfirmation] =
    React.useState(false);
  const [error, setError] = React.useState({});
  const { types: templateTypes, loading: templateTypesLoading } =
    useTemplateTypes();
  const {
    staticApplicableTypeOptions,
    normalizeTemplateType,
    deriveGeneratedType,
    normalizeApplicableTypes,
  } = useTemplateTypeRules();

  const prevSaveLoadingRef = React.useRef(false);
  React.useEffect(() => {
    const wasLoading = prevSaveLoadingRef.current;
    const isLoading = Boolean(createEditTemplateDetails?.loading);
    prevSaveLoadingRef.current = isLoading;

    if (!embedded) return;
    if (email_template_id) return; // embedded flow used for create; don't reset edit mode

    const saveCompleted = wasLoading && !isLoading;
    if (!saveCompleted) return;

    const hasError = Boolean(createEditTemplateDetails?.error);
    if (hasError) return;

    // Successful save: reset the form/stepper so user can upload next template.
    setActiveStep(0);
    handlePageReset();
  }, [createEditTemplateDetails?.error, createEditTemplateDetails?.loading, email_template_id, embedded]);

  React.useEffect(() => {
    if (email_template_id) return;
    const initial = initialTemplateType?.trim();
    if (!initial) return;
    if (templateState.type) return;
    const normalizedType = normalizeTemplateType(initial);
    if (!normalizedType) return;
    const generated_type = deriveGeneratedType(normalizedType);
    const applicable_types = normalizeApplicableTypes(
      [],
      normalizedType,
      generated_type,
    );
    setTemplateState(prev => ({
      ...prev,
      type: normalizedType,
      generated_type,
      applicable_types,
    }));
  }, [
    deriveGeneratedType,
    email_template_id,
    initialTemplateType,
    normalizeApplicableTypes,
    normalizeTemplateType,
    templateState.type,
  ]);

  React.useEffect(() => {
    return () => handlePageReset();
  }, []);

  React.useEffect(() => {
    handleSelectDealer();
  }, [selectedDealerId, email_template_id]);

  React.useEffect(() => {
    handleSaveOnFinish();
  }, [activeStep]);

  React.useEffect(() => {
    handleTemplateEdit();
  }, [template]);

  const handleSelectDealer = () => {
    if (selectedDealerId && email_template_id) {
      dispatch(
        emailCreateEditActions.requestSavedEmailTemplate({
          dealerId: selectedDealerId,
          emailTemplateId: email_template_id,
        }),
      );
    }
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setSkipped(prevSkipped => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const isStepOptional = (step: number) => {
    return false;
  };

  const handleNext = () => {
    let validate = validateSteps();
    if (validate) {
      let newSkipped = skipped;
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setSkipped(newSkipped);
    }
  };

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleSaveConfirmationDialogOpen = () => {
    try {
      setOpenDialogSaveConfirmation(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSaveConfirmationDialogClose = () => {
    try {
      setOpenDialogSaveConfirmation(false);
      setActiveStep(0);
    } catch (error) {
      console.log(error);
    }
  };

  const handlePageReset = () => {
    setTemplateState({
      type: '',
      generated_type: '',
      applicable_types: [],
      name: '',
      subject: '',
      html: '',
      expiration_date: getDefaultExpirationDate(),
      tracking_id: '',
    });
    setHtmlFile(null);
    dispatch(emailCreateEditActions.clearSavedEmailTemplate());
  };

  const requestSaveEmailTemplate = () => {
    try {
      if ((templateState?.html ?? '').trim().length > 0) {
        const { tracking_id, ...rest } = templateState;
        const normalizedType = normalizeTemplateType(rest.type);
        const generatedType =
          rest.generated_type || deriveGeneratedType(normalizedType);
        const applicableTypes = normalizeApplicableTypes(
          rest.applicable_types,
          normalizedType,
          generatedType,
        );
        const data = {
          ...rest,
          type: normalizedType,
          generated_type: generatedType,
          applicable_types: applicableTypes,
          ...(tracking_id && { metadata: { tracking_id } }),
        };
        !email_template_id
          ? dispatch(
              emailCreateEditActions.requestCreateEmailTemplate({
                dealerId: selectedDealerId,
                data,
                history: embedded ? undefined : history,
              }),
            )
          : dispatch(
              emailCreateEditActions.requestUpdateEmailTemplate({
                dealerId: selectedDealerId,
                data,
                emailTemplateId: email_template_id,
                history: embedded ? undefined : history,
              }),
            );
        handleSaveConfirmationDialogClose();
      } else throw new Error();
    } catch (error) {
      console.log(error);
    }
  };

  const handleSaveOnFinish = () => {
    try {
      if (activeStep >= steps.length - 1) {
        handleSaveConfirmationDialogOpen();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleTemplateEdit = () => {
    try {
      if (Object.keys(template).length) {
        const normalizedType = normalizeTemplateType(template.type ?? '');
        const generatedType =
          template.generated_type ?? deriveGeneratedType(normalizedType);
        const applicableTypes = normalizeApplicableTypes(
          template.applicable_types,
          normalizedType,
          generatedType,
        );
        setTemplateState({
          ...template,
          type: normalizedType,
          generated_type: generatedType,
          applicable_types: applicableTypes,
          tracking_id: template.metadata?.tracking_id ?? template.tracking_id ?? '',
        });
        if (template.html && typeof template.html === 'string') {
          const baseName = (template.name || 'email-template')
            .replace(/[<>:"/\\|?*\n\r]+/g, ' ')
            .trim();
          const fileName =
            (baseName || 'email-template') +
            (baseName.toLowerCase().endsWith('.html') ? '' : '.html');
          setHtmlFile(
            new File([template.html], fileName, { type: 'text/html' }),
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const generateUUID = (): string => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return (crypto as Crypto & { randomUUID: () => string }).randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  React.useEffect(() => {
    if (!email_template_id) {
      setTemplateState(prev => ({
        ...prev,
        tracking_id: prev.tracking_id || generateUUID(),
      }));
    }
  }, [email_template_id]);

  const handleTypeChange = (value: string) => {
    const normalizedType = normalizeTemplateType(value);
    const generatedType = deriveGeneratedType(normalizedType);
    const nextApplicableTypes = normalizeApplicableTypes(
      templateState.applicable_types,
      normalizedType,
      generatedType,
    );

    setTemplateState(prev => ({
      ...prev,
      type: normalizedType,
      generated_type: generatedType,
      applicable_types: nextApplicableTypes,
    }));
  };

  const handleApplicableTypesChange = (values: string[]) => {
    setTemplateState(prev => ({
      ...prev,
      applicable_types: normalizeApplicableTypes(
        values,
        prev.type,
        prev.generated_type,
      ),
    }));
  };

  const handleHtmlFileSelect = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const content = e.target?.result as string;
      if (typeof content === 'string') {
        setTemplateState(prev => ({ ...prev, html: content }));
        setHtmlFile(file);
      }
    };
    reader.readAsText(file);
  };

  const isDynamicGenerated =
    String(templateState.generated_type ?? '').toLowerCase() === 'dynamic';
  const useEmbeddedDynamicFlow = embedded && isDynamicGenerated;

  const validateSteps = () => {
    try {
      let errorObject: any = {};
      Object.keys(templateState).map(item => {
        switch (item) {
          case 'name':
          case 'type':
          case 'subject':
          case 'generated_type':
            if (!templateState[item]?.trim()?.length) {
              errorObject[item] = true;
            }
            break;
          case 'html':
            if (!isDynamicGenerated && !templateState[item]?.trim()?.length) {
              errorObject[item] = true;
            }
            break;
          case 'applicable_types':
            if (!Array.isArray(templateState.applicable_types) || !templateState.applicable_types.length) {
              errorObject.applicable_types = true;
            }
            break;

          default:
            break;
        }
      });
      setError(errorObject);

      if (Object.keys(errorObject).length) {
        return false;
      }

      return true;
    } catch (error) {
      console.log(error);
    }
  };

  const stepsContainer = [
    {
      step: 0,
      name: 'Template',
      container: (
        <Step1Container
          templateState={templateState}
          setTemplateState={setTemplateState}
          error={error}
          html={!isDynamicGenerated}
          templateTypes={templateTypes}
          templateTypesLoading={templateTypesLoading}
          staticApplicableTypeOptions={staticApplicableTypeOptions}
          onTypeChange={handleTypeChange}
          onApplicableTypesChange={handleApplicableTypesChange}
          htmlFile={htmlFile}
          onHtmlFileSelect={handleHtmlFileSelect}
          showDynamicGenerationPreferences={useEmbeddedDynamicFlow}
          dealerId={selectedDealerId}
          filterSegmentName={filterSegmentName}
        />
      ),
    },
    {
      step: 1,
      name: 'Preview And Save',
      container: null,
      // container: <Step2Container templateState={templateState} />,
    },
  ];

  const emailTemplateListNav = (location.state || {}) as {
    breadcrumbParentLabel?: string;
    breadcrumbParentPath?: string;
  };
  const breadcrumbParentLabel =
    emailTemplateListNav.breadcrumbParentLabel ?? 'All Email Templates';
  const breadcrumbParentPath =
    emailTemplateListNav.breadcrumbParentPath ?? '/emails-grid';

  const mainContent = (
    <>
      {(createEditTemplateDetails.loading || templateDetails.loading) && (
        <LoadingIndicator />
      )}
      <Box
        component="div"
        sx={
          embedded
            ? {
                flex: 1,
                minHeight: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }
            : { minHeight: '60vh' }
        }
      >
        {!embedded ? (
          <BreadcrumbContainer>
            <Breadcrumbs
              aria-label="breadcrumb"
              separator={<NavigateNextIcon fontSize="small" />}
            >
              <BreadcrumbLinkComponent
                underline="always"
                color="inherit"
                onClick={() => history.push(breadcrumbParentPath)}
              >
                {breadcrumbParentLabel}
              </BreadcrumbLinkComponent>
              <BreadcrumbLinkComponent underline="hover" color="text.primary">
                {email_template_id
                  ? `Edit Email Template ${
                      templateState?.name.trim().length
                        ? `(${templateState?.name})`
                        : ''
                    }`
                  : `Create Email Template`}
              </BreadcrumbLinkComponent>
            </Breadcrumbs>
          </BreadcrumbContainer>
        ) : null}
        {!embedded ? (
          <PageHeader>
            {email_template_id ? `Edit Email Template ` : `Create Email Template`}
          </PageHeader>
        ) : null}

        {!selectedDealerId ? (
          <Typography
            variant="h4"
            gutterBottom
            component="div"
            className={classes.dealerTypography}
          >
            Please select a dealer
          </Typography>
        ) : useEmbeddedDynamicFlow ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {stepsContainer[0].container}
          </Box>
        ) : (
          <>
            <StepperComponent
              activeStep={activeStep}
              steps={steps}
              handleReset={handleReset}
              handleBack={handleBack}
              handleSkip={handleSkip}
              handleNext={handleNext}
              isStepOptional={isStepOptional}
              isStepSkipped={isStepSkipped}
              stepsContainer={stepsContainer}
              replaceFinishButtonLable={'Save Template'}
            />
          </>
        )}
      </Box>
    </>
  );

  return (
    <>
      {!embedded ? (
        <Helmet>
          <title>Email Templates</title>
          <meta name="description" content="Email Templates - Myautoiq" />
        </Helmet>
      ) : null}
      {!embedded ? <PageContainer>{mainContent}</PageContainer> : mainContent}
      <Dialog
        open={openDialogSaveConfirmation}
        onClose={handleSaveConfirmationDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{`Save ${
          templateState?.name || ''
        } email template`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Are you sure you want to save ${
              templateState?.name || ''
            } email template? `}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            onClick={handleSaveConfirmationDialogClose}
            autoFocus
          >
            Cancel
          </Button>
          <Button onClick={() => requestSaveEmailTemplate()}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
